import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReallocationV2StepId, ReallocationV2Store } from '../../state/reallocation-v2.store';

/** One term of the wizard's master equation. */
export interface CalculationTerm {
  /** Column caption. */
  readonly label: string;
  /** The figure. */
  readonly value: number;
  /** The step that DECIDES this term, or null for a term no step owns. Drives the highlight. */
  readonly ownedBy: ReallocationV2StepId | null;
  /**
   * Rendered bold mint — the figure the bar beside it partitions.
   *
   * ⚠️ NOT "the first one", which is what the template used to assume. On a contribution or
   * distribution the account total is printed first for reading order while the AMOUNT is what
   * the runs add up to, so position and emphasis had to come apart.
   */
  readonly emphasis?: boolean;
  /**
   * A longer caption for the HOVER, which has no width limit at all.
   *
   * ⚠️ Optional, and absent on every term whose label already explains itself. "Cash On Hold"
   * does not: it says something is true of the money without saying what, and the answer — a
   * sale under review — is not guessable from three words (AndyP, 2026-09-22).
   *
   * ⚠️ NOT used on the RUN, which is the one place genuinely short of space: a run is sized by
   * its share of the account, and `inside` already drops even the short label the moment it will
   * not fit. Giving it a longer one would push more runs into the legend for no gain.
   */
  readonly tooltipLabel?: string;
  /**
   * A caption for the LEGEND, which has room for more than a run but not for the hover's version.
   *
   * ⚠️ Separate from {@link tooltipLabel} because the two have different budgets, and the legend's
   * is the tighter of them (AndyP, 2026-09-22): it is one `whitespace-nowrap` row carrying every
   * run's caption AND figure, and it only appears when the bar is already too cramped — so it
   * lengthens exactly where space is shortest. "NOT INVESTABLE" there, "NOT IMMEDIATELY
   * INVESTABLE" on the hover.
   */
  readonly legendLabel?: string;
  /** Rendered in red — the figure is over budget. */
  readonly overBudget?: boolean;
  /**
   * Rendered in amber — this money still has to go somewhere.
   *
   * Amber rather than red on purpose. Having money left to place is the NORMAL state for
   * most of the wizard: the advisor sells on step 1 and only reaches the models on step 4,
   * so red would cry wolf for three steps running. It blocks at the allocation step, and
   * that step says so in red itself.
   */
  readonly needsPlacing?: boolean;
  /**
   * Background colour for this term's run in the allocation bar, as a **hex string**.
   *
   * One field so the run and the swatch that names it can never disagree — see `barSegments`.
   *
   * ⚠️ A HEX, not a Tailwind class, and that is on purpose. These are data-driven colours, which
   * is exactly where this config's purge trap bites: an off-scale or not-yet-referenced utility
   * emits no CSS at all and silently applies nothing (see the `w-250` note in
   * tailwind.config.js). A `bg-*` class here also confined the palette to the seven mint shades,
   * and the run needed a tint that is not one of them.
   *
   * Optional because {@link CalculationStripComponent.investableTerm} has none: it is the EMPTY
   * part of the bar, which is the track showing through rather than a run with a fill.
   */
  readonly swatch?: string;
  /**
   * Colour for the label written INSIDE that run, as a **hex string**.
   *
   * ⚠️ It travels with the fill because it is a property OF the fill, not of the strip: the two
   * teals are far enough apart that no single ink is legible on both. White clears AA on #1B7A6D
   * (5.18:1) and fails badly on #76AFA7 (2.48:1). Pairing them here is what stops a later shade
   * change quietly leaving unreadable text behind.
   *
   * ⚠️ WHITE ON BOTH RUNS IS NOT AVAILABLE, and it is worth knowing why rather than trying it
   * again: white only reaches 4.5:1 once the lighter run is darkened to about #298276, and at
   * that point it is 1.12:1 against #1B7A6D -- visually the same colour as the run beside it. It
   * is white-on-both OR two distinguishable teals, not both.
   *
   * A hex like `swatch`, for the same reason: `text-[#0F3B35]` is an arbitrary value and this
   * config's purge trap makes those a silent no-op.
   */
  readonly ink?: string;
}

/** One filled run of the allocation bar. Generated from the terms; never written by hand. */
export interface BarSegment {
  readonly key: string;
  readonly label: string;
  readonly value: number;
  /** Background colour as a hex string, straight off the term. */
  readonly fill: string;
  /** Label colour as a hex string, straight off the term. */
  readonly ink: string;
  /** Width as a percentage of the pool, 0–100. */
  readonly pct: number;
  /** The term's longer caption, for the hover. Absent when the label speaks for itself. */
  readonly tooltipLabel?: string;
  /** The term's caption for the legend, which has less room than the hover. */
  readonly legendLabel?: string;
}

/**
 * The wizard's running figures, rendered once by the shell and carried across every step.
 *
 * ```
 * TOTAL VALUE  CUSTODIAL CASH │ Remaining Positions  Purchases  Protected Cash  Allocated to Models │ Investable
 *      the account as it is   │  step 1     step 2       step 3         step 4                      │ what is left
 * ```
 *
 * Three groups, and the dividers are load-bearing. Only the MIDDLE four partition Total
 * Account Value; Custodial Cash does not, because it is already inside two of them. Keeping it
 * left of the divider is what stops it reading as a fifth slice.
 *
 * **Figures, not arithmetic.** These were laid out as an equation with + and = between them,
 * which invited the advisor to verify a sum rather than take in a state — and buried the one
 * number they can act on at the end of a chain of five. The account total now leads as
 * context, the four components follow in the order the wizard decides them, and Investable
 * sits apart on the right because it is the only one that asks for anything.
 *
 * They do still reconcile — `customHoldingsValue = positions + purchases + cash` and
 * `investable = totalValue − customHoldingsValue` — and a spec asserts it. That invariant is
 * what makes the figures trustworthy side by side; it just is not the reader's job to check.
 *
 * **The point is that there is only one of it.** Steps 1, 2 and 3 each used to re-derive
 * their own summary in markup, which is how step 3's tile came to read
 * `Positions + Protected Cash = Total` for months while `Total` also included buys -- the
 * figures on screen did not sum and nothing caught it (ACMTECH-943). An equation cannot
 * disagree with itself when there is one copy.
 */
@Component({
  selector: 'app-rav2-calculation-strip',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './calculation-strip.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalculationStripComponent {
  readonly store = inject(ReallocationV2Store);

  /** Below this, a figure is treated as zero. Half a cent. */
  private static readonly EPSILON = 0.005;

  /**
   * Every term the request actually has, in equation order.
   *
   * **Purchases and Protected Cash appear only once something is in them.** Both are optional
   * parts of a request: plenty of reallocations sell and re-model without buying anything or
   * setting any cash aside, and for those the strip carried two permanent `$0.00` columns that
   * were never going to say anything else.
   *
   * ⚠️ This reverses an earlier decision, and the cost it was avoiding is real: the row's
   * width and the position of the figures now change when the advisor adds the first buy or
   * sets aside the first dollar. That was judged worse than the noise -- but the original
   * problem was terms appearing as each STEP WAS REACHED, which moved things while the advisor
   * was merely navigating. Keying on content instead means the row only changes when the
   * advisor changes the request, which is a movement they caused and can explain.
   *
   * Remaining Positions and Allocated to Models are NOT conditional. Every request has both --
   * an account always holds something and always ends with some model split, including an
   * empty one -- so a zero there is a real statement about the request rather than an absence.
   *
   * Purchases is dropped for a second and different reason as well: when the request cannot
   * buy at all, the term does not exist rather than merely being empty.
   *
   * ⚠️ Purchases shows `resolvedBuyValue`, not `totalBuyValue`, and Protected Cash shows
   * `totalProtectedCash`. Together those are still exactly `totalBuyValue + cashInput`, so the
   * row partitions the account once -- but the pending-review rows are now counted where they
   * belong.
   *
   * A hand-typed ticker is not a purchase the trade team can act on: it is held as cash until
   * the security master resolves it. Reporting it under Purchases put the strip at odds with
   * the Protected Cash step, which counted the same money in its own total -- the strip read
   * $5,000 protected where the step read $15,000, and neither figure was wrong.
   *
   * 🟢 Which is also why Protected Cash reveals on `totalProtectedCash`: a pending CUSIP makes
   * the column meaningful the moment it is entered on the BUY step, well before the advisor
   * reaches Protected Cash and possibly without them ever setting a dollar aside themselves.
   */
  /**
   * TWO components, in the order the wizard decides them.
   *
   * Investable is deliberately NOT among them — see {@link investableTerm}.
   *
   * ⚠️ It used to be four, plus a Custodial Cash figure outside the divider. Custom Holdings now
   * ABSORBS Purchases and Protected Cash, and Custodial Cash is gone entirely, because cash is
   * opt-in as of the step-1 release control: the sleeve's cash is no longer part of the opening
   * investable balance, so a term stating it explained nothing about the equation.
   *
   * 🟢 The two terms plus Investable reconcile to the account, which is the property worth
   * protecting when anyone edits this list:
   *
   *   CustomHoldings + Allocated + Investable
   *     = (nonCashKept + cashKept + cashInput + buys) + allocated
   *       + (modelSleeveValue + cashReleased + soldValue − cashInput − buys − allocated)
   *     = nonCashKept + soldValue + totalCash + modelSleeveValue        ← the whole account
   *
   * It holds because a partial sell's kept half is derived from its sold half, and
   * `cashKept + cashReleased === totalCash` by construction. Add a term that overlaps another
   * and it stops holding.
   */
  /**
   * The two committed parts of the account, each carrying the fill its bar segment uses.
   *
   * ⚠️ These PARTITION THE ACCOUNT with what is left over:
   *
   *   Allocated to models + Custom Holdings + Left to allocate = the whole account
   *
   * It holds because a partial sell's kept half is derived from its sold half, and
   * `cashKept + cashReleased === totalCash` by construction. Add a term that overlaps another
   * and it stops holding — which is what these two specs exist to catch.
   *
   * ⚠️ Custom Holdings ABSORBS purchases and structured cash, so neither is a term of its own.
   * Both land in the advisor-directed sleeve, and `customHoldingsValue` already carries them.
   * They were briefly separate draws on a pool that excluded Custom Holdings; that version
   * could not show the sleeve at all, which is the one figure this bar is a partition of.
   *
   * ⚠️ The `swatch` lives HERE, on the term, not in the template beside either the figure or the
   * bar. The bar is generated from this same list (see {@link barSegments}), so one field feeds
   * both and a segment cannot end up a different colour from the figure naming it. Two lists
   * would have been two places to keep in step, which is the failure mode this strip has already
   * had once with the term order.
   */
  private readonly allTerms = computed<CalculationTerm[]>(() => {
    if (this.store.isMoveMoney()) return this.moveMoneyTerms();
    return this.reallocationTerms();
  });

  /**
   * The context figures, left of the divider — what the request is measured AGAINST.
   *
   * Types 1 and 2 are measured against the account, so it is the account that leads. Types 3 and
   * 4 are measured against a figure the advisor typed on step 1, and the account's own value is
   * not the budget: a $6,000 contribution to a $2m account has $6,000 to place. Showing the
   * account total there would invite exactly the confusion the strip exists to remove.
   *
   * ⚠️ CUSTODIAL CASH IS GONE from the reallocation side (merge, ACMTECH-970/971). It used to sit
   * here beside the account total, on the reasoning that it was already inside two of the middle
   * terms and so could not be a slice of its own. Releasing cash is now an opt-in decision on
   * step 1, so the figure that mattered stopped being "what the sleeve holds" and became "what
   * this request was given" -- which is inside Custom Holdings and Investable, not beside them.
   */
  /**
   * The funding step shows the account total and NOTHING else — no bar, no remainder.
   *
   * ⚠️ The strip used to be hidden here outright, and the reason still holds for the rest of it:
   * the amount is being SET on this step, so a "left to allocate" box would read the full amount
   * back at the advisor as though something were already outstanding, and the bar has no basis to
   * partition yet. What was missing is the one figure that is true before anything is decided —
   * what the account is worth — which is exactly what an advisor sizing a distribution wants
   * (AndyP, 2026-09-22).
   */
  readonly isContextOnly = computed(() => this.store.currentStepId() === 'funding');

  readonly contextTerms = computed<CalculationTerm[]>(() => {
    const accountTotal = {
      label: 'Total Account Value',
      value: this.store.totalValue(),
      ownedBy: null,
    } as const;

    if (this.store.isMoveMoney()) {
      // Sizing the amount: the account total is the only thing settled, so it carries the row.
      if (this.isContextOnly()) return [{ ...accountTotal, emphasis: true }];

      // ⚠️ THE ACCOUNT TOTAL FIRST, THE EMPHASIS SECOND, and they are deliberately not the same
      // figure. Reading order puts context before subject -- "of this account, this much is
      // moving" -- but the bar partitions the AMOUNT, not the account, so the amount is what
      // gets the bold mint. Bolding the total instead would claim the runs beside it add up to
      // the account, which on a $5,000 distribution from a $557,842 account is off by two orders
      // of magnitude.
      return [
        accountTotal,
        {
          label: this.store.isContribution() ? 'Contribution' : 'Distribution',
          value: this.store.fundingAmount(),
          ownedBy: null,
          emphasis: true,
        },
      ];
    }

    // A reallocation partitions the account itself, so here the two coincide.
    return [{ ...accountTotal, emphasis: true }];
  });

  /**
   * Where a contribution's money goes, or where a distribution's comes from.
   *
   * ⚠️ **Every term is unconditional here**, unlike the reallocation set below where terms
   * appear only once used. The reason is the highlight: each of these steps owns exactly one or
   * two terms, so hiding an empty one would leave the advisor standing on a step with nothing
   * lit up — and "which of these am I deciding right now" is the whole job the strip does.
   *
   * A permanent `$0.00` is also a truer statement for these types than it is for a reallocation:
   * a distribution that takes nothing from cash has genuinely taken nothing from cash, and that
   * is a fact about the request rather than an absent feature.
   *
   * ⚠️ THE RAMP IS THE REALLOCATION'S, STEP FOR STEP, and reused rather than re-chosen. Those
   * three hexes were measured -- against the empty track, which is the constraint that decides
   * them, not against each other -- and inventing a parallel set for these types would have put
   * two teal scales in one wizard for no reason the advisor could see.
   *
   * The ORDER they map onto is what carries meaning: deepest for the part the advisor is
   * actively deciding, lighter for what follows from it. Both families lead with the models, so
   * both open on #1B7A6D.
   *
   *   #1B7A6D  white     the models -- the decision, and the step the wizard opens on
   *   #76AFA7  white     the second source or use
   *   #A7CFC9  #0F3B35   the third; dark ink, because white is unreadable at this lightness
   *
   * ⚠️ White on #76AFA7 is 2.48:1 and FAILS WCAG AA. That was accepted on the reallocation with
   * the ratio in front of us (see the Custom Holdings term below for why dark inks read worse
   * here despite measuring better), and reusing the ramp inherits the decision rather than
   * taking a new one. If it is ever revisited, both families move together.
   */
  private moveMoneyTerms(): CalculationTerm[] {
    if (this.store.isContribution()) {
      return [
        {
          label: 'To Models',
          value: this.store.placedInModels(),
          ownedBy: 'sleeveAmounts',
          swatch: '#1B7A6D',
          ink: '#FFFFFF',
        },
        ...(this.store.hasBuyStep()
          ? [{
            label: 'Purchases',
            value: this.store.resolvedBuyValue(),
            ownedBy: 'buys' as const,
            overBudget: this.store.buysExceedInvestable(),
            swatch: '#76AFA7',
            ink: '#FFFFFF',
          }]
          : []),
        // ⚠️ Keeps the THIRD step of the ramp whether or not Purchases renders. The colour names
        // the term, not its position: a contribution with the buy step flagged off would
        // otherwise shift protected cash up a shade and mean something different on two accounts.
        {
          label: 'Protected Cash',
          value: this.store.totalProtectedCash(),
          ownedBy: 'protectedCash',
          swatch: '#A7CFC9',
          ink: '#0F3B35',
        },
      ];
    }

    // Both of the distribution's raising terms belong to the SAME step, which the sort handles:
    // equal keys keep their written order, so Cash Taken reads before Sale Proceeds — the order
    // the step itself presents them in, cash pinned above the table.
    return [
      {
        label: 'From Models',
        value: this.store.placedInModels(),
        ownedBy: 'sleeveAmounts',
        swatch: '#1B7A6D',
        ink: '#FFFFFF',
      },
      {
        label: 'Cash Taken',
        value: this.store.cashTaken(),
        ownedBy: 'positions',
        swatch: '#76AFA7',
        ink: '#FFFFFF',
      },
      // ⚠️ Cash and sells share a STEP but not a shade. They are two different acts -- money the
      // account already had, against money raised by selling -- and one colour across both would
      // undo the reason they are separate terms at all.
      {
        label: 'Sale Proceeds',
        value: this.store.totalSoldValue(),
        ownedBy: 'positions',
        swatch: '#A7CFC9',
        ink: '#0F3B35',
      },
    ];
  }

  /**
   * ⚠️ DEVELOP'S SET, TAKEN WHOLE (ACMTECH-939/971). Three runs, not the four this branch had:
   * Custom Holdings ABSORBED Purchases and Protected Cash, and Cash On Hold was split out so that
   * money moving WITHIN the sleeve is visible. The colour reasoning in each term is measured
   * rather than chosen -- do not adjust a hex without reading it.
   */
  private reallocationTerms(): CalculationTerm[] {
    return [
      // What the allocation grid literally sums. Binding `investable()` here once said "Allocated
      // to Models" about a figure that also held what the sleeves already had, plus any money the
      // request had not placed — asserting an allocation the advisor never made.
      {
        label: 'Allocated to models',
        value: this.store.allocation().totals.value,
        ownedBy: 'allocation',
        // ⚠️ THE DEEPER of the two teals, and that ordering is the point: this is the part of the
        // account the advisor is actively deciding, so it carries the weight. It was `mint-darker`
        // (#26A896) with white text, which is 2.95:1 and fails AA; this takes white to 5.18.
        swatch: '#1B7A6D',
        ink: '#FFFFFF',
      },
      // Everything landing in the advisor-directed sleeve: positions kept, cash kept, structured
      // cash, and purchases.
      //
      // ⚠️ `customHoldingsValue` already carries kept cash, structured cash and the buys, so do
      // NOT add `nonCashCustomHoldingsValue` or `cashKept` on top; both are already inside it.
      //
      // ⚠️ The over-budget red rides here because this figure carries the buys.
      {
        label: 'Custom Holdings',
        // ⚠️ MINUS the protected cash, which is drawn beside this rather than inside it.
        // `customHoldingsValue` here would count that money twice.
        //
        // 🟢 Custodial cash the advisor KEPT is still in this figure, and belongs there: nobody
        // decided anything about it, so it stays with the securities it sat alongside.
        value: this.store.customHoldingsExcludingCashOnHold(),
        ownedBy: 'positions',
        overBudget: this.store.buysExceedInvestable(),
        // ⚠️ THE LIGHT mint, where this run was neutral grey. Grey was chosen because the sleeve
        // is the part staying as it is -- but grey is also the colour of the EMPTY tail, so the bar
        // read as "39.8% complete" with a long unfilled remainder. Dark mint, light mint and one
        // grey says instead: both of these are the account, one of them is where you are working,
        // and only the pale grey at the end is nothing yet.
        //
        // ⚠️ A LIGHTER TINT OF THE SAME TEAL -- step 4 of the brighten ramp off #1B7A6D
        // (colorkit.co/color/1b7a6d) -- and NOT one of the seven palette mints, which is the
        // reason these fills are hexes now.
        //
        // ⚠️ The measurement that settled it is separation FROM THE EMPTY TRACK, not paleness.
        // This was `mint-lighter` (#C7F3EC), which is 1.06:1 against the track: the same lightness
        // as the empty rail, so the run and the gap read as one continuous band and the sleeve
        // looked like it was not drawn. Two shades before that (`mint-light`, #8FE8DA) failed the
        // opposite way -- Custom Holdings is routinely 70-95% of the bar, so a loud mid-tone put
        // the brightest colour on the part nobody is deciding.
        //
        // #76AFA7 sits between: 1.95 against the track and 2.09 against the dark run. Those two
        // pull in opposite directions, and the tie-breaker is HUE -- this run and the dark one are
        // the same hue and differ only in lightness, so the ratio is what separates them, while
        // the track is neutral grey and reads apart on hue alone.
        //
        // ⚠️ WHITE, AT 2.48:1, WHICH FAILS WCAG AA -- AndyP's call, made with the ratio in front
        // of him, and recorded here so it is not mistaken for an oversight.
        //
        // Two dark inks were tried first and both were rejected on how they LOOKED rather than on
        // how they measured: neutral near-black (`gray-darkest` #27292D, 5.87:1) read as body text
        // borrowed from the table below, and a very dark teal (#0F3B35, 4.98:1) was still called
        // hard to see. That is not the metric misbehaving -- dark-on-mid-teal is a same-hue pairing
        // at 10.5px, and luminance ratio credits it more than the eye does. White breaks on hue AND
        // lightness at once, which is why it reads more easily here despite the worse number.
        //
        // 🟢 The dark ink IS legible where the same figure spills onto the grey track for a narrow
        // run (see `crampedSegments`) -- that label is on near-white, not on the teal, and keeps
        // its own dark colour.
        //
        // ⚠️ There is no version of this that gets white above 4.5:1 while keeping the two runs
        // apart -- see `CalculationTerm.ink`. If this needs improving without moving the colours,
        // the levers are a heavier label weight or a text shadow, neither of which changes the
        // computed ratio.
        swatch: '#76AFA7',
        ink: '#FFFFFF',
      },
      // CASH THIS REQUEST IS HOLDING BACK, drawn beside the sleeve rather than inside it.
      //
      // ⚠️ SPLIT OUT so that money moving WITHIN the sleeve is visible. Selling a security whose
      // proceeds must be held turns a position into protected cash without changing what the sleeve
      // is worth -- so with one combined run the bar did not move at all, and an advisor ticking
      // one of those rows saw nothing happen anywhere on screen. Two runs make the same event read
      // as a shape change: Custom Holdings shrinks, this grows, the pair stays the same width.
      //
      // 🟢 It also makes a whole CLASS of bug visible. Money that leaves Custom Holdings without
      // arriving anywhere now shows as a gap opening rather than as two runs quietly adding up to
      // less than they did -- which is exactly how the reviewable-sale proceeds went missing on a
      // live account before anyone spotted it.
      //
      // ⚠️ MONEY THIS REQUEST CANNOT INVEST, whatever put it there -- a sale whose proceeds are
      // held, a buy whose ticker is unresolved, or the advisor's own set-aside. The three have
      // nothing in common except that state, which is why the run is named for the STATE and not
      // for any of the acts that cause it. It was "Set-Aside Cash" for a day and that read as the
      // advisor's doing, when two of the three are the process's.
      //
      // ⚠️ Custodial cash the advisor simply KEPT is NOT here -- nobody decided anything about it,
      // and giving it a run of its own made leaving money alone look like an act. It stays inside
      // Custom Holdings with the securities it sat beside.
      {
        label: 'Cash On Hold',
        // Three widths, three captions: the run keeps the bare label, the legend gets the short
        // parenthetical, the hover gets the full one. See `tooltipLabel` and `legendLabel`.
        tooltipLabel: 'Cash On Hold (not immediately investable)',
        legendLabel: 'Cash On Hold (not investable)',
        // ⚠️ `cashOnHold`, NOT `totalProtectedCash`. The advisor's step-4 set-aside is deliberately
        // excluded (AndyP, 2026-09-15): it is ordinary cash in the Custom Holdings sleeve, held by
        // instruction, and belongs in the run beside it. What is left here is only money held
        // because something is UNRESOLVED -- a sale whose proceeds cannot be committed, a buy whose
        // ticker nobody has matched -- both of which clear when the Trade Team acts.
        value: this.store.cashOnHold(),
        // Owned by the step that decides most of it. A reviewable sale is ticked on step 1 and
        // lands here too, which is why the two runs move together from there.
        ownedBy: 'protectedCash',
        // ⚠️ A LIGHTER STEP OF THE SAME RAMP, not a new hue. Mint means "the request" throughout
        // this wizard and this is still part of it -- a different hue would read as a different
        // kind of thing rather than as the sleeve's other half.
        //
        // ⚠️ The constraint that decides this value is separation FROM THE EMPTY TRACK, not from
        // the run beside it. `mint-lighter` (#C7F3EC) was measured at 1.06:1 against the track when
        // Custom Holdings was being coloured -- the same lightness as the empty rail, so the run
        // read as undrawn. This sits well above that while still reading as lighter than #76AFA7.
        //
        // Dark ink, unlike the two runs above: at this lightness white is unreadable, and the same
        // near-black the spill labels use is comfortable here.
        swatch: '#A7CFC9',
        ink: '#0F3B35',
      },
    ];
  }

  /**
   * The account as one bar: a segment per committed part, and the rest left empty.
   *
   * ⚠️ **The empty tail IS "Left to allocate".** It is not a segment -- it is the track showing
   * through, which is the whole reason to draw this. Three figures in a row asked the advisor to
   * add up and compare against a fourth; a bar with a gap says "this much has nowhere to go yet"
   * with no arithmetic, and it closes as they work.
   *
   * ⚠️ ORDER IS FIXED HERE -- models, then Custom Holdings, then the gap -- and is NOT the
   * wizard's step order, which would put Custom Holdings first (step 1 before step 2). Two
   * reasons: the empty tail has to be last so it lands beside the figure that names it, so the
   * sequence cannot be a step sequence anyway; and reading "what is being put to work, then what
   * is staying, then what is unplaced" is the sentence the bar is making. `terms()` no longer
   * sorts for the same reason.
   *
   * ⚠️ A zero-value part gets NO segment, so an untouched account is one full Custom Holdings
   * run rather than a run plus an invisible sibling that breaks the rounded end.
   *
   * ⚠️ FULLY ALLOCATED MEANS FULL. With nothing left over the runs reach the end of the track and
   * no red appears -- both follow from the geometry being driven by the same figure the caption
   * is (see the body), rather than from a special case.
   *
   * **Over-committing** puts the excess in a red run taken off the END of the sequence -- the
   * point where the account ran out. It is subtracted from the last part first and works
   * backwards, so the red always sits at the right edge whichever part caused it.
   *
   * Empty when there is nothing to draw: a share of nothing has no meaning, and a bar drawn from
   * a zero denominator would either divide by zero or assert a full one.
   */
  readonly barSegments = computed<BarSegment[]>(() => {
    // ⚠️ **THE GEOMETRY IS DRIVEN BY `investableTerm`, NOT BY THE ACCOUNT TOTAL**, and that is a
    // bug fix rather than a preference. It used to compute its own `committed - totalValue`,
    // which meant the bar and the caption beside it decided "is this over-allocated?" from two
    // different numbers: the caption from `unappliedValue`, which is rounded to the cent, and
    // the bar from raw values that can sit a fraction of a cent apart (Orion reports the account
    // total and the holdings on two bases -- see `investable` in the store). The result was a
    // thin RED SLIVER at the end of a bar whose own caption read "Fully allocated".
    //
    // Taking both from `left` makes that impossible: the red run exists exactly when the caption
    // says Over-allocated, and the gap closes exactly when it says Fully allocated.
    const left = this.investableTerm().value;
    const gap = Math.max(0, left);
    // 🔴 NO RED RUN ON A DISTRIBUTION, and the caption beside the bar is why. Raising more than
    // the target is legitimate there -- the surplus stays in the account as cash -- so the box
    // reads FULLY DISTRIBUTED with a check. A red tail beside that check says the two halves of
    // the strip disagree about whether the request is finished (AndyP, 2026-09-24).
    //
    // 🟢 Zeroing it here is all it takes: `gap` is already 0 when `left` is negative, so the
    // denominator becomes the parts' own sum and the runs fill the bar exactly. Which is the
    // honest picture -- every dollar drawn IS going somewhere, and $4.58 of $10,004.58 is not a
    // slice worth drawing.
    //
    // ⚠️ The run's own label gives the game away too: "Beyond what this account holds". On a
    // distribution the account holds it perfectly well; what it exceeds is the amount being paid
    // out, which is a different claim and not one the bar was ever making.
    const over = this.store.isDistribution() ? 0 : Math.max(0, -left);

    const parts = this.terms().map((t) => ({
      label: t.label,
      tooltipLabel: t.tooltipLabel,
      legendLabel: t.legendLabel,
      value: Math.max(0, t.value),
      fill: t.swatch ?? '#C1C3C9',
      ink: t.ink ?? '#27292D',
    }));

    // ⚠️ `committed + gap`, NOT the account total. The two agree to within Orion's cent, and
    // using the parts' own sum is what guarantees the runs and the gap total exactly 100% --
    // so a fully-allocated bar reaches the end with no hairline of track showing, and a
    // rounding residue lands in the denominator instead of becoming a visible artefact.
    const committed = parts.reduce((sum, p) => sum + p.value, 0);
    const denominator = committed + gap;
    if (denominator <= CalculationStripComponent.EPSILON) return [];

    // Peel the overflow off the tail of the sequence, last part first.
    let unattributed = over;
    const trimmed = parts
      .slice()
      .reverse()
      .map((p) => {
        const red = Math.min(p.value, unattributed);
        unattributed -= red;
        return { ...p, value: p.value - red };
      })
      .reverse();

    const segments: BarSegment[] = trimmed
      .filter((p) => p.value > CalculationStripComponent.EPSILON)
      .map((p) => ({
        key: p.label,
        label: p.label,
        tooltipLabel: p.tooltipLabel,
        legendLabel: p.legendLabel,
        value: p.value,
        fill: p.fill,
        ink: p.ink,
        pct: (p.value / denominator) * 100,
      }));

    if (over > CalculationStripComponent.EPSILON) {
      segments.push({
        key: 'over-allocated',
        label: 'Beyond what this account holds',
        value: over,
        fill: '#9F3734',
        // White on this red is 6.7:1 — the same pairing the sleeve drift labels use.
        ink: '#FFFFFF',
        pct: (over / denominator) * 100,
      });
    }

    return segments;
  });

  /**
   * What is left over, rendered apart from the rest.
   *
   * Set aside because it is the only figure that ASKS for something. The other four report
   * what the request has decided; this one says the request is not finished, and it is what
   * blocks Review. Sitting at the right edge with its own accent means the advisor can find
   * it without reading along the row.
   *
   * Signed: positive is money still looking for a home, negative is more committed to models
   * than the account holds. Both block, and they are different instructions, so the template
   * gives them different colours and different captions.
   */
  readonly investableTerm = computed<CalculationTerm>(() => {
    const value = this.store.unappliedValue();
    return {
      label: 'Investable',
      value,
      ownedBy: null,
      needsPlacing: value >= CalculationStripComponent.EPSILON,
      overBudget: value <= -CalculationStripComponent.EPSILON,
    };
  });

  /**
   * The terms to render — every one of them, in the order the BAR reads.
   *
   * Kept as its own member rather than folding `allTerms` in here, because the partition having
   * a fixed, complete set of parts is a decision worth naming: it is what makes the row add up
   * in every navigation order without any reasoning about what has been visited. (`canJumpTo`
   * lets an advisor jump straight to Allocate to Models, so "what has been reached" was never a
   * sound basis for hiding a term that already had a value.)
   *
   * ⚠️ NO LONGER SORTED by step position. It was (ACMTECH-945), because a hand-ordered list had
   * quietly become wrong when Allocate to Models moved to step 2 and the strip read left-to-right
   * in an order the advisor no longer worked in.
   *
   * That argument does not survive the bar. The gap at the end IS Left to allocate, so it has to
   * be last whatever the step order says, and the sequence stops being a reading of the flow at
   * all -- it becomes "committed to models, staying put, unplaced". Sorting would put Custom
   * Holdings first (step 1) and leave the mint run stranded in the middle, away from the figure
   * it is closing toward. The order is stated in `allTerms` and the reason is on `barSegments`.
   */
  readonly terms = computed<CalculationTerm[]>(() => this.allTerms());

  /**
   * Width at which a run can hold its NAME as well as its figure, as a percentage of the bar.
   *
   * Below it the run shows the figure alone and is identified by its colour; below
   * {@link VALUE_INSIDE_PCT} it cannot hold even that and its figure moves outside — see
   * {@link crampedSegments}.
   *
   * ⚠️ A run narrower than its own text would either clip mid-word or force the flex item wider
   * than its share, which silently falsifies the proportion the bar exists to show. Hence
   * thresholds rather than letting it wrap or overflow.
   */
  private static readonly LABEL_INSIDE_PCT = 24;

  /** Width at which a run can still hold its figure. Below this the figure moves outside. */
  private static readonly VALUE_INSIDE_PCT = 10;

  /** How much of its own text a run has ROOM for, on width alone. */
  private fits(segment: BarSegment): 'full' | 'value' | 'none' {
    if (segment.pct >= CalculationStripComponent.LABEL_INSIDE_PCT) return 'full';
    if (segment.pct >= CalculationStripComponent.VALUE_INSIDE_PCT) return 'value';
    return 'none';
  }

  /**
   * What a run actually SHOWS — which is not the same question as what it has room for.
   *
   * ⚠️ Everything empties when the legend is up. Labelling the wide runs while the narrow one is
   * named underneath produced the worst reading of all: two figures inside the bar, one figure
   * beneath it, and nothing saying the three belong to one total. Either the bar carries the
   * figures or the legend does.
   *
   * 🟢 This is POLICY over {@link fits}, which stays pure geometry. Keeping them apart is what
   * stops the circle — {@link crampedSegments} asks "does it fit", never "what is shown", so
   * emptying the runs cannot feed back into the decision to empty them.
   */
  inside(segment: BarSegment): 'full' | 'value' | 'none' {
    return this.showLegend() ? 'none' : this.fits(segment);
  }

  /**
   * Whether the bar hands its figures to the legend instead of carrying them.
   *
   * True as soon as ONE run cannot hold its own figure. One run failing is enough because the
   * alternative is a split reading, not because the others are also cramped.
   */
  readonly showLegend = computed(() => this.crampedSegments().length > 0);

  /**
   * Every run the legend names, in bar order.
   *
   * ⚠️ ALL of them, not just the ones that would not fit. The first sits at the bar's left edge
   * and the last at its right, so each figure is under the end of its own run — which is what
   * ties a legend entry to a segment when the bar itself is blank.
   *
   * ⚠️ The over-allocated run is excluded here as it is from {@link crampedSegments}: its figure
   * is already beside the bar, in the caption that reads "Over-allocated".
   */
  readonly legendSegments = computed<BarSegment[]>(() =>
    this.barSegments().filter((seg) => seg.key !== 'over-allocated'),
  );

  /**
   * The runs too narrow to hold their own NAME AND figure, which sends every run's to the legend.
   *
   * ⚠️ THE HOLE THIS FILLS. Moving the figures INTO the bar means a tiny run is a MISSING
   * FIGURE, which the old outside legend could not do: a Custom Holdings sleeve of $587.80 on a
   * $552,642.99 account is 0.11% of the bar, so it drew as a hairline with no number anywhere on
   * screen. Between half a cent (below which a run is dropped entirely) and roughly half a
   * percent, the bar was reporting nothing at all.
   *
   * ⚠️ THESE USED TO BE WRITTEN IN THE TRACK, just past the last run, so that in the common case
   * -- a small run followed by a wide gap -- the figure sat immediately beside the run it named.
   * That position is only available when the bar has slack, and it failed exactly when it was
   * needed most: on a 99%-allocated account the track is 1% wide, and the label was clipped
   * mid-glyph to `$11(`. It was `overflow-hidden` with `min-width: 0` on purpose -- the runs are
   * the measurement, the annotation lives in the gap on sufferance -- but degrading to HALF A
   * NUMBER reads as a rendering fault rather than as a deliberate omission, which is the worst of
   * both outcomes.
   *
   * A line beneath the bar cannot be squeezed by the data, so the figure is always readable and
   * the runs never have to give up width to make room for it.
   *
   * 🟢 The line only exists when this list is non-empty, so a strip whose runs all carry their
   * own figures costs no vertical space — which matters in a modal where the step body is the
   * only region that scrolls.
   *
   * ⚠️ The over-allocated run is deliberately excluded. Its own figure is already on screen --
   * the caption beside the bar reads "Over-allocated" with the signed amount -- so a legend entry
   * would state it twice.
   */
  readonly crampedSegments = computed<BarSegment[]>(() =>
    this.barSegments().filter(
      // ⚠️ `!== 'full'`, not `=== 'none'`. The trigger used to be "this run cannot hold its
      // FIGURE", which left the middle case on the bar: a run wide enough for its number but not
      // its name drew a bare `$113,779.50` against no label at all, and the advisor had to infer
      // which part of the account it was from its colour.
      //
      // The bar either carries every run's name and figure or it carries none of them.
      (seg) => seg.key !== 'over-allocated' && this.fits(seg) !== 'full',
    ),
  );

  /** True when this term is the one the current step decides. */
  isCurrent(term: CalculationTerm): boolean {
    return term.ownedBy !== null && term.ownedBy === this.store.currentStepId();
  }

  /**
   * The caption on the right-hand box, which names the STATE rather than the figure.
   *
   * Three states per type, and the verb is what differs: a contribution *places* money it has
   * been given, a distribution *sources* money it has to find, and a reallocation *allocates*
   * what the account already holds. Using one word for all three would make the box read as
   * being about the same operation in every case.
   */
  remainderCaption(): string {
    const term = this.investableTerm();
    if (this.store.isContribution()) {
      // "Contribute", not "place" or "allocate" (AndyP, 2026-09-22). The advisor's mental model
      // is the money coming IN; where it lands inside the account is the mechanism, not the goal.
      // Parallel to the distribution's wording below, so the two types read alike.
      if (term.needsPlacing) return 'Left to contribute';
      if (term.overBudget) return 'Over-contributed';
      return 'Fully contributed';
    }
    if (this.store.isDistribution()) {
      // "Distribute", not "source" or "allocate". The advisor's mental model is the money going
      // out to the client; where it is found inside the account is the mechanism, not the goal.
      // ("Allocate" is worse still — it belongs to the target-allocation vocabulary of a
      // reallocation, which this request does not touch.)
      //
      // ⚠️ THREE STATES, AND A FOURTH WAS TRIED. A "Short by" caption in orange sat in front of
      // this, for the case where the cash was spent and only selling would close the gap. It was
      // reverted (AndyP, 2026-09-24): the row buttons now say what to do, and "short" named a
      // fault where an outstanding balance is simply a request still being filled in.
      if (term.needsPlacing) return 'Left to distribute';
      // ⚠️ NOT 'Over-distributed', and it is no longer an error. A distribution may raise more
      // than it pays out -- the surplus stays in the account as cash -- which is what makes
      // selling one more share a usable remedy. The excess is named beside the caption instead.
      return 'Fully distributed';
    }
    if (term.needsPlacing) return 'Left to allocate';
    if (term.overBudget) return 'Over-allocated';
    // ⚠️ 'Fully allocated', not 'Investable'. This is the word the strip has always SHOWN in the
    // satisfied state; the mismatch was invisible only because this method fed a tooltip on the
    // grey track, and in the satisfied state there is no grey track to hover.
    return 'Fully allocated';
  }

  /** What to do about it — the caption says what this is, the tooltip says how to resolve it. */
  remainderTooltip(): string {
    const term = this.investableTerm();

    // ⚠️ THE SATISFIED STATE HAS A TOOLTIP TOO, and it has to be type-aware for the same reason
    // the caption is: "every dollar of this account is placed" is simply untrue of a
    // distribution, which places nothing and takes money out.
    if (!term.needsPlacing && !term.overBudget) {
      if (this.store.isContribution()) return 'Every dollar of this contribution is placed';
      if (this.store.isDistribution()) return 'The full distribution has been sourced';
      return 'Every dollar of this account is placed';
    }

    if (this.store.isContribution()) {
      return term.needsPlacing
        ? 'Allocate it to a model, buy securities with it, or set it aside as cash'
        : 'More has been placed than this contribution carries';
    }
    if (this.store.isDistribution()) {
      // 🟢 THE TOOLTIP still narrows when the cash is spent, which is the part of the reverted
      // "Short by" state worth keeping: offering "take custodial cash" as one of three remedies
      // when there is none left is the actively unhelpful half.
      if (term.needsPlacing && this.store.distributionCashCannotCover()) {
        return 'Sell positions, or take more from the models';
      }
      return term.needsPlacing
        ? 'Take more from the models, take custodial cash, or sell positions'
        : 'More has been raised than this distribution needs — the surplus stays in the account '
          + 'as cash';
    }
    return term.needsPlacing
      ? 'Put it into a model, buy with it, or protect it as cash'
      : 'More is allocated to models than the account has available';
  }
}
